import React from "react";
import { motion } from "framer-motion";
import { FileText, Shield, UserCheck, AlertCircle, HelpCircle } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

export default function TermsPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const sections = [
    {
      title: "1. Acceptance of Terms",
      content: "By accessing and using HuluOrder, you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you must not use our services.",
      icon: UserCheck
    },
    {
      title: "2. User Accounts",
      content: "To post items or interact with sellers, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.",
      icon: Shield
    },
    {
      title: "3. Listing Guidelines",
      content: "All listings must be accurate, legal, and follow our community standards. We reserve the right to remove any content that violates these terms or is deemed inappropriate.",
      icon: FileText
    },
    {
      title: "4. Prohibited Activities",
      content: "Users may not engage in fraudulent activities, harassment, or the sale of illegal items. Any attempt to compromise the security of our platform will result in immediate termination.",
      icon: AlertCircle
    },
    {
      title: "5. Limitation of Liability",
      content: "HuluOrder is a marketplace platform. We do not own or inspect the items listed. We are not responsible for the quality, safety, or legality of items advertised, or the accuracy of listings.",
      icon: HelpCircle
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gray-50">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-gray-900 via-gray-900 to-green-900/30 -z-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.h1 
              {...fadeIn}
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-400"
            >
              Terms & Conditions
            </motion.h1>
            <motion.p 
              {...fadeIn}
              transition={{ delay: 0.2 }}
              className="text-lg text-gray-400 max-w-2xl mx-auto"
            >
              Please read these terms carefully before using our platform. 
              Last updated: April 2026.
            </motion.p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20 max-w-4xl mx-auto px-6">
          <div className="space-y-12">
            {sections.map((section, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-3">{section.title}</h2>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div 
            {...fadeIn}
            className="mt-20 p-8 rounded-3xl bg-gray-900 text-white border border-gray-800 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl"></div>
            <h2 className="text-2xl font-bold mb-4">Questions about our terms?</h2>
            <p className="text-gray-400 mb-6">
              If you have any questions or concerns regarding these terms, 
              please don't hesitate to reach out to our support team.
            </p>
            <a 
              href="mailto:support@huluorder.com" 
              className="inline-flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl transition-all"
            >
              Contact Support
            </a>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
